/**
 * Power Role - Convert Pillar
 *
 * Conversão entre unidades de potência usando hub-and-spoke (via watt).
 *
 * @example
 * import { convertPower, toBasePower, fromBasePower } from '@attrx/role-morphic/power/convert';
 *
 * convertPower('horsepower_mechanical', 'kilowatt', 1);  // 0.7457
 * toBasePower('kilowatt', 2.5);                          // 2500 (watts)
 * fromBasePower('kilowatt', 2500);                       // 2.5
 */

import { POWER_UNITS, type PowerUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Unidade base para conversões (hub) */
export const POWER_BASE: PowerUnit = "watt";

// =============================================================================
// CORE CONVERSION
// =============================================================================

/**
 * Converte valor para a unidade base (watt)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em watts
 *
 * @example
 * toBasePower('kilowatt', 2.5);             // 2500
 * toBasePower('horsepower_mechanical', 1);  // 745.7
 */
export function toBasePower(variant: PowerUnit, value: number): number {
  const config = POWER_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown power unit: ${variant}`);
  }
  return value * (config.factor ?? 1);
}

/**
 * Converte valor da unidade base (watt) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em watts
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBasePower('kilowatt', 2500);  // 2.5
 * fromBasePower('watt', 100);       // 100
 */
export function fromBasePower(variant: PowerUnit, baseValue: number): number {
  const config = POWER_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown power unit: ${variant}`);
  }
  return baseValue / (config.factor ?? 1);
}

/**
 * Converte entre duas unidades de potência
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertPower('horsepower_mechanical', 'kilowatt', 1);  // 0.7457
 * convertPower('kilowatt', 'watt', 2.5);                 // 2500
 */
export function convertPower(
  from: PowerUnit,
  to: PowerUnit,
  value: number,
): number {
  if (from === to) {
    return value;
  }
  const baseValue = toBasePower(from, value);
  return fromBasePower(to, baseValue);
}

/**
 * Versão safe de convertPower que retorna Result
 *
 * @example
 * tryConvertPower('kilowatt', 'watt', 1);  // { ok: true, value: 1000 }
 * tryConvertPower('invalid', 'watt', 1);   // { ok: false, error: '...' }
 */
export function tryConvertPower(
  from: PowerUnit,
  to: PowerUnit,
  value: number,
): Result<number> {
  try {
    return { ok: true, value: convertPower(from, to, value) };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Verifica se uma variante existe
 *
 * @example
 * hasPowerVariant('kilowatt');  // true
 * hasPowerVariant('invalid');   // false
 */
export function hasPowerVariant(variant: string): variant is PowerUnit {
  return variant in POWER_UNITS;
}

/**
 * Retorna lista de todas as variantes disponíveis
 *
 * @example
 * getPowerVariants();  // ['gigawatt', 'megawatt', 'kilowatt', ...]
 */
export function getPowerVariants(): PowerUnit[] {
  return Object.keys(POWER_UNITS) as PowerUnit[];
}
